'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Link } from '@/src/i18n/navigation';
import { useTranslations } from 'next-intl';
import { Anchor, Heading, Text } from '@zillow/constellation-10';
import { useTheme } from 'next-themes';
import { useCurrentArea } from '@/src/lib/hooks/query';
import type { AreaResponse } from '@/src/lib/api';
import { getTopCitiesForState } from '@/src/lib/utils/topCities';
import { MIN_LG } from '@/src/lib/breakpoints';
import ListingSearch from '@/src/components/ListingSearch';
import notFoundMissingHomeDark from '@/images/illustrations/notFoundMissingHomeDark.png';
import notFoundMissingHome from '@/images/illustrations/notFoundMissingHome.png';
import {
  artwork,
  artworkFrame,
  cityLink,
  columnsRow,
  contentWrapper,
  heading,
  heroSection,
  linkList,
  nearMeLink,
  nearMeLinksList,
  nearMeLinksNav,
  otherLink,
  pageContainer,
  searchSection,
  searchWrapper,
  subColumn,
  subColumnHeading,
  subtext,
  textColumn,
  topSection,
} from '@/src/styles/pages/notFound.styles';

const DEFAULT_STATE = 'CA';
const PREFERS_DARK = '(prefers-color-scheme: dark)';
const ARTWORK_SIZES = `${MIN_LG} 52vw, 100vw`;

const NEAR_ME_SRP_LINKS = [
  { messageKey: 'apartmentsNearArea', searchSlug: 'apartments-for-rent' },
  { messageKey: 'housesNearArea', searchSlug: 'houses-for-rent' },
  { messageKey: 'affordableApartmentsNearArea', searchSlug: 'affordable-apartments-for-rent' },
] as const;
const MAX_NEAR_ME_SRP_LINKS = 4;
const VISIBLE_NEAR_ME_SRP_LINKS = NEAR_ME_SRP_LINKS.slice(0, MAX_NEAR_ME_SRP_LINKS);

type ArtworkSource = string | { src: string };
type NotFoundContentProps = {
  headingText?: string;
  hideArtwork?: boolean;
  nearMeArea?: AreaResponse | null;
  subtext?: string;
};

const getArtworkSrc = (source: ArtworkSource) => (typeof source === 'string' ? source : source.src);

function titleCaseResourceId(resourceId: string) {
  const segments = resourceId.split('-');
  const areaSegments = segments.length > 1 ? segments.slice(0, -1) : segments;
  return areaSegments.map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1)).join(' ');
}

function getAreaDisplayName(area: AreaResponse) {
  return (
    area.city ||
    area.name ||
    area.neighborhood ||
    area.zip ||
    (area.resourceId ? titleCaseResourceId(area.resourceId) : null)
  );
}

export function NotFoundContent({
  headingText,
  hideArtwork = false,
  nearMeArea,
  subtext: subtextOverride,
}: NotFoundContentProps) {
  const { data: currentArea } = useCurrentArea();
  const { resolvedTheme } = useTheme();
  const t = useTranslations('notFound');
  const [hasMounted, setHasMounted] = useState(false);
  const searchNearMeArea = nearMeArea ?? currentArea ?? null;
  const stateAbbreviation = currentArea?.state || nearMeArea?.state || DEFAULT_STATE;
  const topCities = getTopCitiesForState(stateAbbreviation);
  const activeArtwork = hasMounted && resolvedTheme === 'dark' ? notFoundMissingHomeDark : notFoundMissingHome;
  const nearMeAreaName = nearMeArea ? getAreaDisplayName(nearMeArea) : null;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <div className={pageContainer}>
      <div className={contentWrapper}>
        <div className={topSection}>
          <div className={textColumn}>
            <div className={heroSection}>
              <Heading level={1} textStyle="heading-lg" className={heading}>
                {headingText ?? t('heading')}
              </Heading>
              <Text asChild textStyle="body-lg" color="text.subtle">
                <p className={subtext}>{subtextOverride ?? t('subtext')}</p>
              </Text>
            </div>

            <div className={searchSection}>
              <div className={searchWrapper}>
                <ListingSearch
                  locationAnalyticsSurface="notFound"
                  trackingContext={null}
                  size="lg"
                  mode="navigate"
                  nearMeArea={searchNearMeArea}
                />
              </div>

              {nearMeArea?.resourceId && nearMeAreaName && (
                <nav aria-label={t('nearMeLinksAria', { areaName: nearMeAreaName })} className={nearMeLinksNav}>
                  <ul className={nearMeLinksList}>
                    {VISIBLE_NEAR_ME_SRP_LINKS.map((link) => (
                      <li key={link.searchSlug}>
                        <Anchor asChild>
                          <Link href={`/${nearMeArea.resourceId}/${link.searchSlug}`} className={nearMeLink}>
                            {t(link.messageKey, { areaName: nearMeAreaName })}
                          </Link>
                        </Anchor>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </div>
          </div>

          {!hideArtwork && (
            <div className={artworkFrame} aria-hidden="true">
              <picture>
                {!hasMounted && <source media={PREFERS_DARK} srcSet={getArtworkSrc(notFoundMissingHomeDark)} />}
                <Image src={activeArtwork} alt="" className={artwork} priority sizes={ARTWORK_SIZES} />
              </picture>
            </div>
          )}
        </div>

        <div className={columnsRow}>
          {topCities.length > 0 && (
            <div className={subColumn}>
              <Heading level={2} textStyle="heading-xs" className={subColumnHeading}>
                {t('searchHotRentals')}
              </Heading>
              <ul className={linkList}>
                {topCities.map((city) => (
                  <li key={city.uri}>
                    <Anchor asChild>
                      <Link href={city.uri} className={cityLink}>
                        {city.name}
                      </Link>
                    </Anchor>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={subColumn}>
            <Heading level={2} textStyle="heading-xs" className={subColumnHeading}>
              {t('otherThingsToDo')}
            </Heading>
            <Anchor href="mailto:info@hotpads.com" className={otherLink}>
              {t('reportIssue')}
            </Anchor>
            <Anchor asChild>
              <Link href="/" className={otherLink}>
                {t('goToHomepage')}
              </Link>
            </Anchor>
          </div>
        </div>
      </div>
    </div>
  );
}
